import React from 'react';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { Button } from '@oysterjs/ui/button';
import { Spinner } from '@oysterjs/ui/spinner';

import {
  AmsLogStatus,
  AmsNotLoggedReason,
  type OutboundEmailAmsLogFieldsFragment,
  type OutboundEmailFieldsFragment
} from '~/api/graphql';
import type { PushableEmail } from '~/components/ams-email-log/push-email-to-ams';
import { Dash } from '~/components/clients/clients-columns';
import { rowDateTimeFormatter } from '~/components/table-formatters';
import { integrationName } from '~/lib/integrations';

const ONLY_SENDER_OR_ADMIN = 'Only the sender or an admin can log this email.';

/** Why an email wasn't logged, as Outgoing's hover says it. */
const NOT_LOGGED_REASON: Record<AmsNotLoggedReason, (amsName: string) => string> = {
  [AmsNotLoggedReason.SeveralMatch]: () => 'Several clients match this address',
  [AmsNotLoggedReason.RecipientsMatchSeveral]: () => 'Recipients match several clients',
  [AmsNotLoggedReason.NoMatch]: () => 'No client matches this address',
  [AmsNotLoggedReason.Skipped]: () => 'Skipped before sending',
  [AmsNotLoggedReason.UnsupportedAms]: (amsName) => `${amsName} isn't supported yet`,
  [AmsNotLoggedReason.UnsupportedConnection]: (amsName) =>
    `${amsName} needs an SDK connection. Ask an admin to switch it in Settings > Integrations`,
  [AmsNotLoggedReason.SentFromOutlook]: () => 'Sent from Outlook',
  [AmsNotLoggedReason.LoggingOff]: () => 'Logging was off when it was sent'
};

const IS_SEVERAL_MATCH: ReadonlySet<AmsNotLoggedReason> = new Set([
  AmsNotLoggedReason.SeveralMatch,
  AmsNotLoggedReason.RecipientsMatchSeveral
]);

/** The hover on a Not logged email's Push button, or on its "-" for whoever can't push. */
const notLoggedHover = (amsLog: OutboundEmailAmsLogFieldsFragment, amsName: string) => {
  const reason = amsLog.notLoggedReason;
  const because = reason ? `${NOT_LOGGED_REASON[reason](amsName)}.` : '';
  if (!amsLog.canPush) {
    return ['Not logged.', because, ONLY_SENDER_OR_ADMIN].filter(Boolean).join(' ');
  }
  if (!reason) {
    return `Not logged. Push to log it to ${amsName}.`;
  }
  return IS_SEVERAL_MATCH.has(reason)
    ? `${because} Push to choose one and log it to ${amsName}.`
    : `${because} Push to log it to ${amsName}.`;
};

/** The hover on a failed or unconfirmed email's warning, and on its Push again button. */
const warningHover = (amsLog: OutboundEmailAmsLogFieldsFragment, amsName: string) => {
  const warning =
    amsLog.status === AmsLogStatus.CouldNotConfirm
      ? amsLog.canPush
        ? "Cara couldn't confirm the first attempt landed. Pushing again may add a second copy."
        : "Cara couldn't confirm the first attempt landed."
      : [`Couldn't log to ${amsName}.`, amsLog.failureMessage].filter(Boolean).join(' ');
  return amsLog.canPush ? warning : `${warning} ${ONLY_SENDER_OR_ADMIN}`;
};

const loggedHover = (amsLog: OutboundEmailAmsLogFieldsFragment, amsName: string) => {
  const where = amsLog.accountDisplayName ? `${amsLog.accountDisplayName} in ${amsName}` : amsName;
  const loggedAt = amsLog.loggedAt ? new Date(amsLog.loggedAt) : null;
  const isLoggedAtValid = !!loggedAt && !Number.isNaN(loggedAt.getTime());
  const when = isLoggedAtValid ? ` on ${rowDateTimeFormatter.format(loggedAt)}` : '';
  return `Logged to ${where}${when}.`;
};

/**
 * An icon or "-" whose meaning is in its hover, said to screen readers too unless
 * isReadElsewhere, as by a Push again button beside it.
 */
const WithHover = (props: {
  hover: string;
  isReadElsewhere?: boolean;
  className?: string;
  children: React.ReactNode;
}) => (
  <span title={props.hover} className={props.className}>
    {props.children}
    {!props.isReadElsewhere && <span className="sr-only">{props.hover}</span>}
  </span>
);

/** Push's compact size keeps the row at the height of a one-line row. */
const PushButton = (props: {
  hover: string;
  label?: string;
  onPush: () => void;
  children: React.ReactNode;
}) => (
  <Button
    outline
    title={props.hover}
    aria-label={props.label}
    className="shrink-0 px-2 py-0.5 text-xs/5 sm:px-2 sm:py-0.5 sm:text-xs/5"
    onClick={props.onPush}
  >
    {props.children}
  </Button>
);

/**
 * Outgoing's AMS cell: an icon or Push, the detail in the hover. Push shows only when the
 * server says the reader may.
 */
export const OutboxAmsCell = (props: {
  amsLog: OutboundEmailAmsLogFieldsFragment | null;
  /** Whether the AMS log couldn't be read. */
  isUnread?: boolean;
  /** The email's subject, so a list of Push buttons says which email each pushes. */
  subject?: string;
  onPush?: () => void;
}) => {
  const { amsLog, onPush, subject } = props;
  if (props.isUnread) {
    return (
      <WithHover
        hover="Cara couldn't load whether this email was logged. Reload to try again."
        className="cursor-default text-neutral-500 dark:text-neutral-400"
      >
        Unknown
      </WithHover>
    );
  }
  if (!amsLog) return <Dash />;
  const amsName = integrationName(amsLog.integrationType);
  const pushAction = amsLog.canPush ? onPush : undefined;
  const pushLabel = (again: string) =>
    subject ? `Push "${subject}"${again} to ${amsName}` : undefined;

  switch (amsLog.status) {
    case AmsLogStatus.Scheduled:
      return <Dash />;
    case AmsLogStatus.Logging:
      return (
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-neutral-500 dark:text-neutral-400">
          <Spinner size="size-3.5" color="neutral" />
          Logging…
        </span>
      );
    case AmsLogStatus.Logged:
      return (
        <WithHover hover={loggedHover(amsLog, amsName)} className="inline-flex cursor-default">
          <CheckCircleIcon aria-hidden className="size-5 text-green-600 dark:text-green-400" />
        </WithHover>
      );
    case AmsLogStatus.NotLogged: {
      const hover = notLoggedHover(amsLog, amsName);
      return pushAction ? (
        <PushButton hover={hover} label={pushLabel('')} onPush={pushAction}>
          Push
        </PushButton>
      ) : (
        <WithHover hover={hover} className="cursor-default">
          <Dash />
        </WithHover>
      );
    }
    case AmsLogStatus.CouldNotLog:
    case AmsLogStatus.CouldNotConfirm: {
      const hover = warningHover(amsLog, amsName);
      return (
        <div className="flex items-center gap-1.5">
          {pushAction && (
            <PushButton hover={hover} label={pushLabel(' again')} onPush={pushAction}>
              Push again
            </PushButton>
          )}
          <WithHover
            hover={hover}
            isReadElsewhere={!!pushAction}
            className="inline-flex cursor-default"
          >
            <ExclamationTriangleIcon
              aria-hidden
              className="size-5 text-amber-500 dark:text-amber-400"
            />
          </WithHover>
        </div>
      );
    }
    case AmsLogStatus.Unsupported: {
      const reason = amsLog.notLoggedReason ?? AmsNotLoggedReason.UnsupportedAms;
      return (
        <WithHover
          hover={`Not logged. ${NOT_LOGGED_REASON[reason](amsName)}.`}
          className="cursor-default"
        >
          <Dash />
        </WithHover>
      );
    }
  }
};

/** A sent email in Outgoing, as the push flow takes it. */
export const pushableOutboundEmail = (
  email: OutboundEmailFieldsFragment,
  amsLog: OutboundEmailAmsLogFieldsFragment
): PushableEmail => ({
  id: email.id,
  subject: email.subject,
  recipients: email.to.map((address) => address.name || address.email).join(', '),
  toAddresses: email.to.map((address) => address.email),
  sentAt: email.sentAt ?? null,
  amsLog
});
